import { useState } from "react";
import { Box, Editable, EditableInput, EditablePreview, HStack, Icon, Stack } from "@chakra-ui/react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import CDS from "design-system";

import AutosaveTrixField from "./AutosaveTrixField";
import { DEFAULT_RESPONSE_SCREEN_HEADER, DEFAULT_RESPONSE_SCREEN_TEXT } from "../constants";

interface Props {
  header: string | null;
  text: string | null;
  onHeaderChange: (nextValue: string) => void;
  onTextChange: (nextValue: string) => void;
}

// Collapsed by default — response-screen copy is edited rarely and stays out of the way of field work.
// Empty inputs render the defaults so placeholders match what respondents actually see.
export default function ResponseScreenCard({ header, text, onHeaderChange, onTextChange }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="base" p={4} bg="bg.secondary">
      <HStack
        as="button"
        type="button"
        w="100%"
        spacing={2}
        justify="space-between"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <HStack spacing={2}>
          <CDS.Text14 fontWeight="bold" color="content.primary">Response screen</CDS.Text14>
          <CDS.Text12 color="content.tertiary">(shown after submitting)</CDS.Text12>
        </HStack>
        <HStack spacing={2} minW={0}>
          {!isExpanded && (
            <CDS.Text12 color="content.tertiary" noOfLines={1}>
              {header || DEFAULT_RESPONSE_SCREEN_HEADER}
            </CDS.Text12>
          )}
          <Icon as={isExpanded ? FiChevronUp : FiChevronDown} boxSize={4} color="content.tertiary" />
        </HStack>
      </HStack>

      {isExpanded && (
        <Stack spacing={1} mt={2}>
          {/* Heading typography mirrors FormSubmittedView so the builder reads as the real post-submit screen. */}
          <Editable
            defaultValue={header ?? ""}
            placeholder={DEFAULT_RESPONSE_SCREEN_HEADER}
            onSubmit={(next) => onHeaderChange(next.trim())}
            submitOnBlur
          >
            <EditablePreview
              w="100%"
              fontSize="2xl"
              fontWeight="bold"
              lineHeight="short"
              color={header ? "content.primary" : "content.tertiary"}
              _hover={{ bg: "bg.tertiary", cursor: "text" }}
              borderRadius="base"
              px={2}
              py={1}
            />
            <EditableInput fontSize="2xl" fontWeight="bold" px={2} py={1} borderRadius="base" />
          </Editable>

          <AutosaveTrixField
            value={text}
            onChange={onTextChange}
            placeholder={DEFAULT_RESPONSE_SCREEN_TEXT}
          />
        </Stack>
      )}
    </Box>
  );
}
